import FAQ from '@/components/faq';
import {
  Container,
  Eyebrow,
  SectionHead,
} from '@/components/layout-primitives';
import { HowItWorks } from '@/components/how-it-works';
import { BackgroundVideo } from '@/components/background-video';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  ArrowLongRightIcon,
  ArrowUpRightIcon,
  CheckIcon,
} from '@heroicons/react/16/solid';
import { BOOKING_URL, CONTACT_URL, buttonBase } from '@/lib/bw';
import { BookingMonth } from '@/components/booking-month';
import { useDocument } from '@/lib/use-document';
import { HOME_ROUTE } from '@/lib/seo';

/* Base URL for the Lulu's storefront photo. Already carries a `?v=` cache key,
   so the width transform appended to it is `&width=`, not `?width=`. */
const LULU_STOREFRONT_IMAGE =
  'https://cdn.shopify.com/s/files/1/0674/6033/3844/files/316301135_573605274769122_462216403077211709_n-2.jpg?v=1675798354';

const capabilities = [
  'Ecommerce',
  'Shopify POS',
  'Google Business Profile',
  'SEO',
  'Discounts & Promos',
  'Google Analytics',
  'Loyalty & Referrals',
  'Production Reports',
  '3rd Party Integrations',
  'Subscriptions',
  'Email & SMS Marketing',
  'UGC Product Reviews',
  'Direct Mail Marketing',
  'International Shipping',
  'Custom Reporting',
  'Payment Processing',
  'Pickup & Local Delivery',
  'Domain Management',
  'Product Availability',
  'Social Proof',
];

export interface CustomerReview {
  /** Verbatim review text. Never paraphrase or shorten mid-sentence. */
  quote: string;
  /** Reviewer name exactly as it appears on the listing. */
  author: string;
  /** The platform it was published on. Rendered on the card -- a review must
   *  never be credited to a platform it did not come from. */
  source: 'Google' | 'Yelp';
  /** Optional substring of `quote` to highlight. Must appear in it verbatim. */
  highlight?: string;
  rating?: number;
}

/**
 * Real customer reviews of Lulu's Sweets Boutique, supplied by the client and
 * reproduced verbatim -- including original capitalisation and punctuation.
 * Platform metadata (Google's "Take out" / "$50-100" chips, Yelp's location and
 * review-count lines, photo counts, dates) is not part of what the reviewer
 * wrote and is excluded.
 *
 * `source` is per-review and rendered on the card. These come from two
 * different platforms and neither may be credited to the other.
 *
 * `author` holds the name as published on the listing; the card renders it
 * abbreviated (see shortenName). Yelp already publishes first name + initial,
 * so those pass through unchanged.
 *
 * All are 5-star per the client. The card still only draws stars when `rating`
 * is explicitly set, so an unrated entry can never render an invented score.
 */
const customerReviews: CustomerReview[] = [
  {
    author: 'Megan Jones',
    source: 'Google',
    quote:
      'I live out of state but wanted to order something for a relative’s birthday. Website was easy to navigate and pickup was simple!',
    highlight: 'Website was easy to navigate and pickup was simple!',
    rating: 5,
  },
  {
    author: 'Jessica Whayne',
    source: 'Google',
    quote:
      'Easy process to order online and customize cake order! I was able to provide color direction and pick the flavors. I added my own cherries. Would order again!',
    highlight: 'Easy process to order online and customize cake order!',
    rating: 5,
  },
  {
    author: 'Lauren Malavich',
    source: 'Google',
    quote:
      'The cake was delicious and gorgeous! I haven’t seen anything like it before. Everything was exactly as I had hoped for. Ordering online was easy and picking the cake up was flawless.',
    highlight: 'Ordering online was easy and picking the cake up was flawless.',
    rating: 5,
  },
  {
    author: 'Eli B.',
    source: 'Yelp',
    quote:
      'We had a great experience ordering a birthday cake. I loved the flavor and size options, online ordering was super easy, pickup was friendly - and most importantly the cake was delicious!!',
    highlight: 'online ordering was super easy',
    rating: 5,
  },
  {
    author: 'Michael M.',
    source: 'Yelp',
    quote:
      'I live in Pittsburgh and truly appreciated how easy it was to understand their pricing and decorating process thanks to their website. It was so simple.',
    highlight:
      'how easy it was to understand their pricing and decorating process thanks to their website',
    rating: 5,
  },
];

interface PricingTier {
  id: string;
  name: string;
  price: string;
  /** e.g. "/month". Omit for tiers without a fixed price. */
  cadence?: string;
  summary: string;
  features: string[];
  cta: { label: string; href: string };
  /** Gets the yellow button. Only one tier should set this -- the design
   *  system allows a single primary button per view region. */
  featured?: boolean;
  note?: string;
}

/**
 * The published offer. Both tiers are the studio's own copy, not placeholders.
 *
 * These terms are quoted in two other places -- the announcement bar and the
 * FAQ -- so a change here needs the same change there. The FAQ is the one that
 * states the contract terms in full; this note is the short form of it.
 */
const pricingTiers: PricingTier[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: '$500',
    cadence: '/month',
    summary:
      'A new or refreshed Shopify store built and maintained on one affordable monthly plan.',
    features: [
      'New Shopify build or migration, nothing upfront',
      'Unlimited maintenance and content updates*',
      'Shopify POS integration (optional)',
      'Email marketing automations',
      'Local delivery & store pickup',
      'Security and platform updates',
      'Technical SEO',
      'Unlimited bug fixes',
      'Referral & loyalty program',
      'Reviews & UGC',
      'Quarterly strategy meetings',
      '100% satisfaction guaranteed',
    ],
    cta: { label: 'Get started', href: BOOKING_URL },
    featured: true,
    note: '12-month minimum to start, then we earn your business month-to-month. *Requests are completed one at a time in order of priority, with an average 2-business-day turnaround.',
  },
  {
    id: 'growth',
    name: 'Growth',
    price: "Let's talk",
    summary:
      'For stores that need more than the essentials: deeper integrations, custom automations, email marketing campaigns, and more.',
    features: [
      'Everything in Starter',
      'Custom integrations and automations',
      'Email marketing campaigns',
      'Custom reporting and dashboards',
      'Multiple store locations',
      'Priority support',
    ],
    cta: { label: 'Book a call', href: BOOKING_URL },
    note: '',
  },
];

const stats = [
  { value: '5,678+', label: 'Online orders' },
  { value: '+36%', label: 'YoY growth in online revenue (avg. since launch)' },
  { value: '4M+', label: 'Google search impressions' },
  { value: '1,585+', label: 'Orders attributed to email marketing' },
];

/**
 * "Megan Jones" -> "Megan J." Keeps a reviewer's surname off a marketing page
 * while staying recognisable to them. The first name is title-cased, since
 * Google display names are free-form and a lowercase one would otherwise
 * render as "jane d.". Names already in this form pass through unchanged.
 */
function shortenName(full: string): string {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  if (parts.length === 0) return full;
  const first = titleCase(parts[0]);
  if (parts.length === 1) return first;
  return `${first} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
}

/** Splits a quote around its highlighted phrase so the mark stays verbatim. */
function ReviewQuote({ review }: { review: CustomerReview }) {
  if (!review.highlight || !review.quote.includes(review.highlight)) {
    return <>{review.quote}</>;
  }
  const [before, ...rest] = review.quote.split(review.highlight);
  return (
    <>
      {before}
      <mark className="bg-bw-yellow text-bw-ink">{review.highlight}</mark>
      {rest.join(review.highlight)}
    </>
  );
}

/* Yellow is a surface, never type on white — so the rating renders in ink. */
function StarRating({
  className = 'h-5 w-5',
  justify = 'justify-center',
  count = 5,
}: {
  className?: string;
  /** Justify utility for the row. Takes responsive variants, since several
   *  of these centre on mobile and align left once the column is wide. */
  justify?: string;
  count?: number;
}) {
  return (
    <div className={`flex gap-0.5 ${justify}`}>
      <span className="sr-only">{count} out of 5 stars</span>
      {Array.from({ length: count }, (_, i) => (
        <svg
          key={i}
          aria-hidden="true"
          focusable="false"
          viewBox="0 0 24 24"
          fill="currentColor"
          className={`${className} text-bw-ink`}
        >
          <path
            fillRule="evenodd"
            d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z"
            clipRule="evenodd"
          />
        </svg>
      ))}
    </div>
  );
}

/**
 * Section background rhythm. No two neighbours share a surface, so every
 * section reads as its own band:
 *
 *   Hero          bw-cream   <- the only section that takes cream as a surface
 *   Marquee       bw-ink
 *   How it works  white
 *   Our work      ink-100
 *   Pricing       white
 *   Contact       bw-yellow
 *   FAQ           ink-050
 *   Footer        bw-ink
 *
 * Exactly one 2px ink rule sits on each seam. Borders belong to the ink,
 * cream and yellow bands (border-y-2); the white and near-white bands own
 * none, because their neighbours already draw both of their edges. Adding a
 * border to a section whose neighbour has one stacks two rules into a 4px
 * line -- invisible when the two surfaces match, obvious once they don't.
 */
export function Home() {
  useDocument(HOME_ROUTE);

  return (
    <main className="bg-white">
      {/* ---------------------------------------------------------------- Hero */}
      {/* overflow-hidden so the grid behind the mascot can spread past the
          container and simply be clipped by the band, instead of adding
          horizontal page scroll. Without it the grid's extent is capped by
          whatever the narrowest viewport can hold. */}
      <section className="overflow-hidden bg-bw-cream pt-10 pb-16 md:pb-20 md:pt-20">
        <Container>
          <div className="grid items-center gap-12 lg:grid-cols-[1.15fr_.85fr]">
            {/* Centred on mobile: the mascot below is centred, and a
                left-aligned column above it left the whole screen lopsided.
                Reverts to left once the two columns sit side by side. */}
            <div className="flex flex-col items-center gap-6 text-center lg:items-start lg:text-left">
              {/* Deliberately outside the button vocabulary. A pill with a 2px
                  ink border, a fill and a hard shadow is this system's button,
                  right down to the press travelling into that shadow -- as a
                  chip it read as a control nobody could click.

                  So: a hairline rather than 2px, ink-300 rather than ink, 8px
                  off the radius scale rather than a pill, no fill and no
                  shadow. Enough of a frame to hold the line together, none of
                  the signals that mean pressable. */}
              <span className="bw-eyebrow inline-flex items-center gap-2.5 rounded-[8px] border border-ink-300 px-3 py-2 text-ink-700">
                {/* Solid dot with an expanding ring behind it. Pulsing the dot
                    itself faded the status indicator out entirely on every
                    cycle; the ring signals "live" while the dot stays legible. */}
                <span
                  aria-hidden="true"
                  className="relative flex h-2.5 w-2.5 items-center justify-center"
                >
                  <span className="absolute inline-flex h-full w-full rounded-full bg-status-success opacity-60 motion-safe:animate-ping" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-status-success" />
                </span>
                <span>
                  Now booking <BookingMonth /> projects
                </span>
              </span>

              <h1 className="bw-display text-[44px] leading-[1.05] text-bw-ink md:text-[58px]">
                Managed ecommerce websites
              </h1>

              {/* Non-breaking space so the last line can never be "rate."
                  alone. Tuning max-width instead would only hold for one
                  viewport, and not at all while the fallback font is still
                  swapping in. */}
              <p className="max-w-[520px] text-[19px] leading-[1.6] text-ink-700">
                We build, launch, and manage Shopify stores for growing
                businesses. No upfront cost, no hourly bills. Just one
                monthly&nbsp;rate.
              </p>

              {/* Full-width while stacked. Pill buttons sized to their own
                  labels left two different widths ragged down the screen. */}
              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${buttonBase} group w-full bg-bw-yellow text-bw-ink sm:w-auto`}
                >
                  Book a call
                  <ArrowLongRightIcon
                    aria-hidden="true"
                    className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1"
                  />
                </a>
                <a
                  href={CONTACT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${buttonBase} w-full bg-white text-bw-ink sm:w-auto`}
                >
                  Contact us
                </a>
              </div>

              <div className="flex flex-col items-center gap-2 pt-2 lg:items-start">
                <StarRating justify="justify-center lg:justify-start" />
                <p className="text-[17px] font-bold text-bw-ink">
                  &quot;helped my business grow tremendously!&quot;
                </p>
                <p className="text-[15px] text-ink-500">
                  &mdash; Rosanna, Lulu&apos;s Sweets Boutique &middot;{' '}
                  <span className="font-bold text-bw-ink">$532,000+</span> in
                  online sales
                </p>
              </div>
            </div>

            {/* Free-standing mascot, no frame. The design system treats the
                artwork itself as the graphic interest, so nothing boxes it in.
                The whole group is nudged left so the mascot sits off the
                column's centre line. */}
            <div className="flex justify-center">
              <div className="relative isolate lg:-translate-x-6">
                {/* Faint grid sitting behind the artwork. `isolate` on the
                    wrapper keeps the negative z-index inside this group rather
                    than dropping it behind the section background. */}
                <span
                  aria-hidden="true"
                  /* The element's box is the grid's extent, so these insets are
                     how far the pattern spreads across the hero -- nothing to
                     do with cell size, which lives in .bw-grid. The section
                     clips them, so they can exceed the viewport safely. */
                  className="bw-grid absolute -inset-x-16 -inset-y-24 -z-10 sm:-inset-x-40 sm:-inset-y-32 lg:-inset-x-64 lg:-inset-y-48"
                />
                {/* Floating drift is a deliberate exception to the system's
                    motion rules, which otherwise let only the seal loop. */}
                <img
                  src="/mascot-main-white-body.png"
                  alt="Mr. Butter, the Butter Websites mascot, holding a keyboard"
                  height={810}
                  width={902}
                  fetchPriority="high"
                  decoding="async"
                  className="h-auto w-[280px] animate-bw-float motion-reduce:animate-none sm:w-[340px] lg:w-[360px] xl:w-[400px]"
                />

                {/* Shopify Partners lockup as a sticker card. It is dark
                    artwork, so it needs the white surface to read. */}
                <div className="absolute -right-4 -top-6 rounded-[12px] border-2 border-bw-ink bg-white px-3.5 py-2.5 shadow-sticker lg:-right-6 lg:-top-8 xl:-right-12">
                  <img
                    src="/ShopifyPartners_Primary.png"
                    alt="Shopify Partners"
                    height={40}
                    width={150}
                    decoding="async"
                    className="h-auto w-[104px] lg:w-[120px]"
                  />
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ------------------------------------------------------------ Marquee */}
      {/* Full-bleed ink band. Yellow on ink is an approved pairing; yellow on
          white is not, which is why this lives on a dark band.
          The track carries the list twice so the -50% shift loops seamlessly.
          The second copy is hidden from assistive tech, and the whole thing
          stops under prefers-reduced-motion -- continuous horizontal motion is
          a vestibular trigger, not just a taste question. */}
      <section
        aria-label="What we handle"
        className="overflow-hidden border-y-2 border-bw-ink bg-bw-ink py-5"
      >
        <div className="flex w-max animate-bw-marquee motion-reduce:animate-none">
          {[0, 1].map((copy) => (
            <ul
              key={copy}
              aria-hidden={copy === 1 || undefined}
              className="flex items-center"
            >
              {capabilities.map((item) => (
                <li key={item} className="flex items-center">
                  <span className="bw-eyebrow whitespace-nowrap px-8 text-[14px] text-bw-yellow">
                    {item}
                  </span>
                  {/* Asterisk drawn rather than typed: the display face sets
                      `*` high against cap height, so a glyph would ride above
                      the centre line of the row it separates. Three bars at
                      60 degrees, white against the yellow items. */}
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="h-3 w-3 shrink-0 text-white"
                  >
                    <rect x="10.5" y="2" width="3" height="20" rx="1.5" />
                    <rect
                      x="10.5"
                      y="2"
                      width="3"
                      height="20"
                      rx="1.5"
                      transform="rotate(60 12 12)"
                    />
                    <rect
                      x="10.5"
                      y="2"
                      width="3"
                      height="20"
                      rx="1.5"
                      transform="rotate(120 12 12)"
                    />
                  </svg>
                </li>
              ))}
            </ul>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------- How it works */}
      <section
        id="how-it-works"
        className="scroll-mt-[92px] bg-white py-16 md:py-24"
      >
        <Container>
          <SectionHead
            eyebrow="Three steps"
            title="How it works"
            lead="We'll be your personal on-call web team."
          />

          {/* The guarantee line is part of the section design and now lives
              inside HowItWorks, so it is not repeated here. */}
          <HowItWorks />
        </Container>
      </section>

      {/* ----------------------------------------------------------- Our work */}
      {/* This band carries both rules for itself and for the white sections on
          either side of it. */}
      <section
        id="work"
        className="scroll-mt-[92px] border-y-2 border-bw-ink bg-ink-100 py-16 md:py-24"
      >
        <Container>
          <SectionHead eyebrow="Our work" title="Sweet, sweet results" />

          <div className="mt-12 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
            <div className="flex flex-col justify-between gap-6 rounded-[20px] border-2 border-bw-ink bg-white p-6 shadow-sticker sm:p-8">
              <div>
                {/* Label and button centre on mobile; the paragraph does not.
                    Nine lines of centred prose is harder to read than it is
                    tidy, so long copy keeps its flush left edge throughout. */}
                <div className="text-center lg:text-left">
                  <Eyebrow>Bakery</Eyebrow>
                </div>
                <p className="mt-4 text-[17px] leading-[1.6] text-ink-700 sm:text-[19px]">
                  Lulu&apos;s Sweets Boutique, an independent bakery in West
                  Chester, Ohio, has done{' '}
                  <span className="bw-display bg-bw-yellow px-1.5 text-[19px] text-bw-ink">
                    $532,000+
                  </span>{' '}
                  in{' '}
                  <strong className="font-bold text-bw-ink">
                    direct online sales
                  </strong>{' '}
                  since publishing their new website. We continue to support
                  them with ongoing maintenance, strategy, automation, and
                  marketing services.
                </p>
              </div>
              <div className="flex justify-center lg:justify-start">
                <a
                  href="https://lulussweetsboutique.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${buttonBase} w-full bg-white text-bw-ink sm:w-auto`}
                >
                  Visit website
                  <ArrowUpRightIcon aria-hidden="true" className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* A real <img> rather than a CSS background. The master is a sharp
                1440x1800, and it was never being upscaled -- but as a
                background the browser fetched all 375KB of it and squashed it
                into a 470x292 box in one step, a 3x reduction done with the
                cheap filter browsers use for backgrounds. That is where the
                fuzz came from.

                next/image used to do the resampling. Shopify's CDN does it now:
                `&width=` is a supported transform, so the srcset below asks it
                for the four sizes this slot actually renders at rather than
                shipping the 1440px master to a phone.

                `sizes` mirrors the real layout: the column is a fixed ~470px
                once the container caps at 1200, ~40vw between 1024 and there,
                and full width when the grid collapses. */}
            <div className="relative min-h-[280px] overflow-hidden rounded-[20px] border-2 border-bw-ink shadow-sticker">
              <img
                src={`${LULU_STOREFRONT_IMAGE}&width=960`}
                srcSet={[480, 720, 960, 1440]
                  .map((w) => `${LULU_STOREFRONT_IMAGE}&width=${w} ${w}w`)
                  .join(', ')}
                sizes="(min-width: 1200px) 480px, (min-width: 1024px) 40vw, 100vw"
                alt="The team behind the counter at Lulu's Sweets Boutique"
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover object-center"
              />
            </div>
          </div>
        </Container>

        {/* Stat cards rather than a full-bleed ink band. The marquee above and
            the footer below are both ink, so a third ink slab here read as one
            too many; cards also match the card language the rest of this
            section already uses. Figures are ink, not yellow -- yellow type is
            only legible on ink, which is why it worked on the old band. */}
        <Container className="mt-6">
          {/* Two-up on mobile rather than four stacked full-width cards, which
              ran 585px tall for four short numbers. The figure steps down to
              28px so the longest value still clears the narrower card. */}
          <dl className="mt-6 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="flex flex-col-reverse justify-end gap-2 rounded-[20px] border-2 border-bw-ink bg-white p-4 text-center shadow-sticker sm:p-6 sm:text-left"
              >
                {/* DOM order is term then description, as <dl> requires;
                    flex-col-reverse puts the figure on top visually without
                    needing an aria-hidden duplicate of the label. */}
                <dt className="text-[13px] leading-[1.5] text-ink-500 sm:text-[15px]">
                  {stat.label}
                </dt>
                <dd className="bw-display text-[28px] leading-none text-bw-ink sm:text-[40px] md:text-[44px]">
                  {stat.value}
                </dd>
              </div>
            ))}
          </dl>
        </Container>

        {/* Video + quote, spanning the same width as the case study row above.
            Column ratio widened back to mirror that row now that the media is
            16:9 -- the narrow slot existed only for the portrait cake photo and
            would have cropped roughly half the frame off this. */}
        <Container className="mt-6">
          <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
            <div
              role="img"
              aria-label="Inside Lulu's Sweets Boutique: cupcakes, decorated cookies and boxed treats in the display case"
              className="relative min-h-[340px] overflow-hidden rounded-[20px] border-2 border-bw-ink shadow-sticker"
            >
              <BackgroundVideo
                src="https://cdn.shopify.com/videos/c/o/v/5df9944c603f4ec99af2175159684efc.mp4"
                poster="/lulu-video-poster.jpg"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>

            <figure className="rounded-[20px] border-2 border-bw-ink bg-white p-8 shadow-sticker md:p-12">
              <StarRating className="h-6 w-6" />
              <blockquote className="mt-6 text-center text-[19px] leading-[1.6] text-ink-700">
                Taylor and Kate helped us create the website of our dreams for
                our new business. They took the time to research our business
                and listen to our vision and delivered a beautiful and
                functional website on time!{' '}
                <mark className="bg-bw-yellow text-bw-ink">
                  The website has helped us grow the business tremendously since
                  going live.
                </mark>{' '}
                We couldn&apos;t be happier with our experience with Butter
                Websites!
              </blockquote>
              <figcaption className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
                <Avatar className="h-12 w-12 border-2 border-bw-ink">
                  <AvatarImage
                    src="https://cdn.shopify.com/s/files/1/0674/6033/3844/files/322692661_2349749938527746_6102235445283175816_n.jpg?v=1675798293"
                    alt=""
                  />
                  <AvatarFallback>RR</AvatarFallback>
                </Avatar>
                <div className="text-center sm:text-left">
                  <p className="font-display text-[15px] font-bold text-bw-ink">
                    Rosanna Ruwe
                  </p>
                  <p className="text-[15px] text-ink-500">
                    Owner, Lulu&apos;s Sweets Boutique
                  </p>
                </div>
              </figcaption>
            </figure>
          </div>
        </Container>

        {/* Their customers on their website. Renders only once real reviews
            are supplied -- see the customerReviews note above. */}
        {customerReviews.length > 0 ? (
          <Container className="mt-16">
            <div className="flex flex-col items-center gap-3 text-center">
              <Eyebrow>What their customers say</Eyebrow>
              <h3 className="bw-display text-[27px] leading-[1.15] text-bw-ink md:text-[34px]">
                A good website shows up in the reviews
              </h3>
            </div>

            {/* Flex-wrap rather than a grid: with an odd number of reviews a
                grid strands the last card alone in its own row, whereas
                justify-center balances the trailing row. */}
            <ul className="mx-auto mt-10 flex max-w-[1120px] flex-wrap justify-center gap-6">
              {customerReviews.map((review) => (
                <li
                  key={`${review.author}-${review.quote.slice(0, 24)}`}
                  className="flex w-full flex-col gap-4 rounded-[20px] border-2 border-bw-ink bg-white p-6 shadow-sticker md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]"
                >
                  {/* Stars render only when the rating is known. Defaulting to
                      five would be inventing a rating the reviewer never gave. */}
                  {review.rating ? (
                    <StarRating
                      className="h-4 w-4"
                      justify="justify-start"
                      count={review.rating}
                    />
                  ) : null}
                  <blockquote className="text-[15px] leading-[1.6] text-ink-700">
                    &ldquo;
                    <ReviewQuote review={review} />
                    &rdquo;
                  </blockquote>
                  <figcaption className="mt-auto flex flex-col gap-0.5">
                    <span className="font-display text-[15px] font-bold text-bw-ink">
                      {shortenName(review.author)}
                    </span>
                    {/* Business name dropped: the section sits inside the
                        Lulu's block and the stat band above it is already
                        labelled with them, so repeating it on five cards only
                        forced the line to wrap. */}
                    <span className="bw-eyebrow text-ink-500">
                      {review.source} review
                    </span>
                  </figcaption>
                </li>
              ))}
            </ul>
          </Container>
        ) : null}
      </section>

      {/* ----------------------------------------------------------- Pricing */}
      <section id="pricing" className="scroll-mt-[92px] py-16 md:py-24">
        <Container>
          <SectionHead
            eyebrow="Pricing"
            title="What it costs"
            lead="No upfront build cost. Month-to-month, cancel anytime after the first year."
          />

          {/* Asymmetric: the featured tier carries far more features, so it
              takes the wider column and lists them two-up. Equal columns forced
              a 12-item single-file list and made the whole section tall. */}
          <div className="mx-auto mt-12 grid max-w-[1080px] gap-6 lg:grid-cols-[1.5fr_1fr]">
            {pricingTiers.map((tier) => {
              // Featured tier uses the design system's inverse surface, which
              // gives the section a focal point and lets the price run yellow.
              const ink = tier.featured;
              return (
                <div
                  key={tier.id}
                  className={`flex flex-col gap-5 rounded-[20px] border-2 border-bw-ink p-6 shadow-sticker sm:p-8 ${
                    ink ? 'bg-bw-ink' : 'bg-white'
                  }`}
                >
                  {/* Header centres on mobile, the feature list below never
                      does -- a centred checklist loses the column its ticks
                      line up on. */}
                  <div className="flex flex-col items-center gap-2.5 text-center sm:items-start sm:text-left">
                    <p
                      className={`bw-eyebrow ${ink ? 'text-bw-yellow' : 'text-ink-500'}`}
                    >
                      {tier.name}
                    </p>
                    <p className="flex items-baseline gap-1.5">
                      <span
                        className={`bw-display text-[44px] leading-none md:text-[52px] ${
                          ink ? 'text-bw-yellow' : 'text-bw-ink'
                        }`}
                      >
                        {tier.price}
                      </span>
                      {tier.cadence ? (
                        <span
                          className={`text-[17px] ${ink ? 'text-ink-300' : 'text-ink-500'}`}
                        >
                          {tier.cadence}
                        </span>
                      ) : null}
                    </p>
                    <p
                      className={`max-w-[46ch] text-[15px] leading-[1.6] ${
                        ink ? 'text-ink-300' : 'text-ink-700'
                      }`}
                    >
                      {tier.summary}
                    </p>
                  </div>

                  <ul
                    className={`grid gap-x-6 gap-y-2.5 border-t-2 pt-5 ${
                      ink ? 'border-ink-700' : 'border-ink-200'
                    } ${tier.features.length > 6 ? 'sm:grid-cols-2' : ''}`}
                  >
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2.5">
                        <CheckIcon
                          aria-hidden="true"
                          className={`mt-1 h-4 w-4 shrink-0 ${
                            ink ? 'text-bw-yellow' : 'text-bw-ink'
                          }`}
                        />
                        <span
                          className={`text-[15px] leading-[1.5] ${
                            ink ? 'text-white' : 'text-ink-700'
                          }`}
                        >
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto flex flex-col gap-3 pt-1">
                    <a
                      href={tier.cta.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      /* Brand yellow stays with the featured tier so it keeps
                         the section's single primary button; the other takes
                         the pale cream, which reads as a step below it. */
                      className={`${buttonBase} w-full text-bw-ink ${
                        ink ? 'bg-bw-yellow' : 'bg-white'
                      }`}
                    >
                      {tier.cta.label}
                    </a>
                    {tier.note ? (
                      <p
                        className={`text-[13px] leading-[1.5] ${
                          ink ? 'text-ink-300' : 'text-ink-500'
                        }`}
                      >
                        {tier.note}
                      </p>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ----------------------------------------------------------- Contact */}
      <section className="border-y-2 border-bw-ink bg-bw-yellow py-16 md:py-24">
        <Container>
          <div className="flex flex-col items-center gap-8 lg:flex-row lg:justify-between">
            <div className="flex flex-col items-center gap-4 text-center lg:items-start lg:text-left">
              <p className="bw-eyebrow text-ink-700">Contact us</p>
              <h2 className="bw-display text-[34px] leading-[1.1] text-bw-ink md:text-[44px]">
                Get a <i>butter</i> website
              </h2>
              <p className="max-w-[520px] text-[19px] leading-[1.6] text-ink-700">
                Let&apos;s talk about your business. Don&apos;t let a half-baked
                website hold you back.
              </p>
            </div>

            {/* Same full-width treatment as the hero pair: content-sized pills
                stacked at two different widths read as a mistake. */}
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
              <a
                href={CONTACT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`${buttonBase} w-full bg-bw-ink text-white sm:w-auto`}
              >
                Contact us
              </a>
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`${buttonBase} w-full bg-white text-bw-ink sm:w-auto`}
              >
                Book a call
              </a>
            </div>
          </div>
        </Container>
      </section>

      {/* --------------------------------------------------------------- FAQ */}
      {/* Tinted so the white accordion cards read as cards rather than
          dissolving into the page, and so the run into the ink footer steps
          down instead of jumping. */}
      <section id="faq" className="scroll-mt-[92px] bg-ink-050 py-16 md:py-24">
        <Container>
          <SectionHead eyebrow="FAQ" title="Frequently asked questions" />
          <FAQ />
        </Container>
      </section>
    </main>
  );
}
